/**
 * 数据加载钩子与日期格式化。
 *
 * 背景：每个页面都要「进入时加载、出错可重试、提交后刷新」，列表还要按游标加载更多。
 * 设计意图：
 *   - useLoad：deps 变化时重新加载；组件卸载或 deps 再次变化后返回的旧结果直接丢弃，不会覆盖新数据。
 *   - usePaged：第一页随 deps 加载，loadMore 带上一页的 next_cursor；reload 从头再来。
 * 关键约束：fn 里抛出的错误放进 error，由页面决定怎么展示；这里不弹 Toast，避免同一个错误提示两遍。
 */
import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react'
import { intlTag, type Locale } from '@ap/i18n'

/**
 * 后端连不上期间自动重试确认登录态。
 *
 * 背景：控制台和管理后台启动时调 /me；后端重启或入口代理 502 时页面进入 offline 状态，由这里负责恢复，
 *   恢复后会话还是原来那条，用户不用重新登录。
 * 设计意图：退避 2、4、8 秒，之后每 15 秒一次；窗口重新获得焦点或浏览器报告网络恢复时立刻重试。
 * 约束：只在 active 为 true 时运行；retry 由调用方实现（再调一次 /me 并更新状态），每轮只发一个请求，
 *   请求的是本地自家接口，不涉及计费资源。
 */
export function useOfflineRetry(active: boolean, retry: () => void): void {
  const retryRef = useRef(retry)
  retryRef.current = retry
  useEffect(() => {
    if (!active) return
    let attempt = 0
    let timer: ReturnType<typeof setTimeout>
    const schedule = () => {
      timer = setTimeout(() => {
        attempt += 1
        retryRef.current()
        schedule()
      }, Math.min(2000 * 2 ** attempt, 15000))
    }
    const now = () => retryRef.current()
    schedule()
    window.addEventListener('focus', now)
    window.addEventListener('online', now)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('focus', now)
      window.removeEventListener('online', now)
    }
  }, [active])
}

export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fn()
      .then((v) => {
        if (alive) setData(v)
      })
      .catch((e) => {
        if (!alive) return
        console.error('[ui] 加载失败', e)
        setError(e)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  const reload = useCallback(() => setTick((x) => x + 1), [])
  return { data, error, loading, reload, setData }
}

export function usePaged<T, Cur = string>(
  fetchPage: (cursor: Cur | null) => Promise<{ data: T[]; next_cursor: Cur | null }>,
  deps: DependencyList,
) {
  const [items, setItems] = useState<T[] | null>(null)
  const [cursor, setCursor] = useState<Cur | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const gen = useRef(0)
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage

  const load = useCallback(async (from: Cur | null, append: boolean) => {
    const my = ++gen.current
    setLoading(true)
    setError(null)
    try {
      const page = await fetchRef.current(from)
      if (my !== gen.current) return
      setItems((xs) => (append && xs ? [...xs, ...page.data] : page.data))
      setCursor(page.next_cursor)
    } catch (e) {
      if (my !== gen.current) return
      console.error('[ui] 列表加载失败', e)
      setError(e)
      if (!append) setItems([])
    } finally {
      if (my === gen.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    setItems(null)
    void load(null, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return {
    items,
    loading,
    error,
    hasMore: cursor !== null && cursor !== undefined,
    loadMore: () => void load(cursor, true),
    reload: () => void load(null, false),
  }
}

export function fmtDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(intlTag(locale), { year: 'numeric', month: '2-digit', day: '2-digit' })
}

export function fmtDateTime(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(intlTag(locale), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 用量汇总的时间范围：最近 n 个 UTC 日（含今天）。
 * 后端按天分组用的是 date_trunc('day', created_at)，数据库时区是 UTC，前端也按 UTC 日对齐，
 * 否则美洲时区的用户会看到日期整体错一天。
 */
export function lastDays(n: number): { start: string; end: string } {
  const end = new Date()
  end.setUTCHours(24, 0, 0, 0)
  const start = new Date(end)
  start.setUTCDate(start.getUTCDate() - n)
  return { start: start.toISOString(), end: end.toISOString() }
}

/** 把汇总里按天的行补齐成连续 n 个 UTC 日（没有调用的日子补 0），label 是 MM-DD。 */
export function fillDays<T extends { [k: string]: unknown }>(rows: T[], n: number, pick: (r: T) => number) {
  const byDay = new Map<string, number>()
  for (const r of rows) {
    const d = new Date(String(r.day ?? ''))
    if (Number.isNaN(d.getTime())) continue
    const key = d.toISOString().slice(0, 10)
    byDay.set(key, (byDay.get(key) ?? 0) + pick(r))
  }
  const out: { key: string; label: string; value: number }[] = []
  const cur = new Date()
  cur.setUTCHours(0, 0, 0, 0)
  cur.setUTCDate(cur.getUTCDate() - (n - 1))
  for (let i = 0; i < n; i++) {
    const key = cur.toISOString().slice(0, 10)
    out.push({ key, label: key.slice(5), value: byDay.get(key) ?? 0 })
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return out
}
